import { useState } from 'react';
import { Filter } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { useDateFormat } from '@/lib/dates';
import { cn } from '@/lib/utils';

interface DateColumnFilterProps {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  onApply: (dateFrom: Date | undefined, dateTo: Date | undefined) => void;
}

/**
 * Date-range filter for table columns. The calendar renders inline in the
 * filter popover — nesting a second popover here caused the whole stack to
 * dismiss when interacting with the calendar's month/year dropdowns.
 */
export function DateColumnFilter({
  dateFrom,
  dateTo,
  onApply,
}: DateColumnFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>();
  const { format } = useDateFormat();

  const hasActiveFilter = dateFrom || dateTo;

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setRange(dateFrom || dateTo ? { from: dateFrom, to: dateTo } : undefined);
    }
    setIsOpen(open);
  };

  const handleApply = () => {
    onApply(range?.from, range?.to ?? range?.from);
    setIsOpen(false);
  };

  const setPreset = (from: Date, to: Date) => setRange({ from, to });
  const today = () => new Date();

  const rangeLabel = range?.from
    ? `${format(range.from)} – ${range.to ? format(range.to) : '…'}`
    : 'No period selected';

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-6 px-1 -ml-2",
            hasActiveFilter && "text-primary"
          )}
        >
          <Filter className={cn("h-3 w-3", hasActiveFilter && "fill-primary")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <h4 className="text-sm font-medium">Filter by date</h4>
          <span className="text-xs text-muted-foreground">{rangeLabel}</span>
        </div>

        <Calendar
          mode="range"
          captionLayout="dropdown"
          selected={range}
          onSelect={setRange}
          defaultMonth={range?.from ?? dateFrom}
        />

        {/* Quick ranges */}
        <div className="grid grid-cols-4 gap-1.5 border-t px-3 py-2">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              const now = today();
              setPreset(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), now);
            }}
          >
            7 days
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              const now = today();
              setPreset(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), now);
            }}
          >
            30 days
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              const now = today();
              setPreset(
                new Date(now.getFullYear(), now.getMonth(), 1),
                new Date(now.getFullYear(), now.getMonth() + 1, 0),
              );
            }}
          >
            Month
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              const now = today();
              setPreset(new Date(now.getFullYear(), 0, 1), new Date(now.getFullYear(), 11, 31));
            }}
          >
            Year
          </Button>
        </div>

        {/* Footer actions */}
        <div className="flex gap-2 border-t px-3 py-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          {range?.from && (
            <Button variant="outline" size="sm" onClick={() => setRange(undefined)}>
              Clear
            </Button>
          )}
          <Button size="sm" className="flex-1" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
